import React, { useCallback, useEffect, useId, useMemo, useRef, useState } from 'react';
import { FileText, X, Clock, CheckCircle2, AlertTriangle } from 'lucide-react';
import { Button, Field, Input } from '@/components/eds';
import {
  paymentService,
  type Invoice,
  type ManualPaymentInstructions,
  type ManualPaymentSubmission,
} from '@/services/paymentService';
import {
  PROOF_ACCEPT,
  PROOF_MAX_BYTES,
  PENDING_LABEL,
  checkProofFile,
  formatBytes,
  submitPaymentEvidence,
  submissionStatusLabel,
} from '@/lib/paymentEvidence';

/*
 * R11P GEOLOGICAL P0.6-NITA-02A — investor manual-payment EVIDENCE (Geo-API RC2).
 *
 * The investor has already paid outside the Portal. Here they submit evidence of
 * that payment: the proof file is uploaded first (POST /manual-payments/proof),
 * then the submission references the RETURNED file id (POST /manual-payments).
 * The result is "Pending Finance verification" — never paid/confirmed. Finance
 * confirms or rejects in the Administration Portal; there is no investor-side
 * confirmation and no online payment here.
 */

// Invoice states that can still receive payment evidence (mirrors the API).
const PAYABLE = new Set(['draft', 'issued', 'partially_paid', 'overdue']);

const money = (amount: number, currency: string) => {
  try {
    return new Intl.NumberFormat(undefined, { style: 'currency', currency: currency || 'GHS' }).format(amount);
  } catch {
    return `${currency || 'GHS'} ${amount.toFixed(2)}`;
  }
};

interface Props {
  invoices: Invoice[];
  invoicesLoaded: boolean;
}

export const PaymentEvidenceForm: React.FC<Props> = ({ invoices, invoicesLoaded }) => {
  const uid = useId().replace(/:/g, '');
  const ids = {
    invoice: `pe-invoice-${uid}`, amount: `pe-amount-${uid}`, ref: `pe-ref-${uid}`, payer: `pe-payer-${uid}`,
    phone: `pe-phone-${uid}`, when: `pe-when-${uid}`, note: `pe-note-${uid}`, file: `pe-file-${uid}`,
  };
  const payable = useMemo(() => invoices.filter((i) => PAYABLE.has(i.status)), [invoices]);

  const [instructions, setInstructions] = useState<ManualPaymentInstructions | null>(null);
  const [instructionsError, setInstructionsError] = useState(false);
  const [mine, setMine] = useState<ManualPaymentSubmission[] | null>(null);
  const [mineError, setMineError] = useState(false);

  const [invoiceId, setInvoiceId] = useState('');
  const [amount, setAmount] = useState('');
  const [providerTxnRef, setProviderTxnRef] = useState('');
  const [payerName, setPayerName] = useState('');
  const [payerPhone, setPayerPhone] = useState('');
  const [paidAt, setPaidAt] = useState('');
  const [note, setNote] = useState('');
  const [file, setFile] = useState<File | null>(null);
  const [fileError, setFileError] = useState<string | null>(null);
  const [reuseProofFileId, setReuseProofFileId] = useState<string | null>(null);

  const [phase, setPhase] = useState<'idle' | 'uploading' | 'submitting'>('idle');
  const [formError, setFormError] = useState<string | null>(null);
  const [submitted, setSubmitted] = useState<{ reference: string } | null>(null);
  const fileInput = useRef<HTMLInputElement>(null);
  const resultRef = useRef<HTMLDivElement>(null);

  const loadMine = useCallback(async () => {
    const r = await paymentService.listMyManualPayments();
    if (r.error || !r.data) { setMineError(true); return; }
    setMineError(false); setMine(r.data);
  }, []);

  useEffect(() => {
    paymentService.getManualPaymentInstructions().then((r) => {
      if (r.error || !r.data) setInstructionsError(true); else setInstructions(r.data);
    });
    void loadMine();
  }, [loadMine]);

  // Default to the first payable invoice and its outstanding amount (from the API).
  useEffect(() => {
    if (!invoiceId && payable[0]) setInvoiceId(payable[0].id);
  }, [payable, invoiceId]);
  const selected = payable.find((i) => i.id === invoiceId) || null;
  useEffect(() => {
    if (!selected) return;
    const due = selected.balanceDue != null && selected.balanceDue > 0 ? selected.balanceDue : selected.total;
    setAmount(due > 0 ? String(due) : '');
  }, [selected?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const onPickFile = (f: File | null) => {
    setFormError(null);
    setReuseProofFileId(null);           // a new file replaces any proof already stored
    if (!f) { setFile(null); setFileError(null); return; }
    const check = checkProofFile(f);
    setFile(f);
    setFileError('message' in check ? check.message : null);
  };
  const removeFile = () => {
    setFile(null); setFileError(null); setReuseProofFileId(null);
    if (fileInput.current) fileInput.current.value = '';
    fileInput.current?.focus();
  };

  const busy = phase !== 'idle';
  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (busy) return;
    setFormError(null); setSubmitted(null);
    if (!reuseProofFileId) {
      const check = checkProofFile(file);
      if ('message' in check) { setFileError(check.message); fileInput.current?.focus(); return; }
    }
    const result = await submitPaymentEvidence<ManualPaymentSubmission>({
      uploadProof: async (f) => { setPhase('uploading'); return paymentService.uploadProof(f); },
      submit: async (payload) => { setPhase('submitting'); return paymentService.submitManualPayment(payload); },
    }, {
      file,
      reuseProofFileId,
      details: {
        invoiceId,
        amountClaimed: Number(amount),
        providerTxnRef: providerTxnRef.trim() || undefined,
        payerName: payerName.trim() || undefined,
        payerPhone: payerPhone.trim() || undefined,
        paymentDatetime: paidAt ? new Date(paidAt).toISOString() : undefined,
        applicantNote: note.trim() || undefined,
      },
    });
    setPhase('idle');
    if (result.stage === 'submitted') {
      setSubmitted({ reference: result.submission.payment_reference });
      setFile(null); setReuseProofFileId(null); setProviderTxnRef(''); setNote(''); setPaidAt('');
      if (fileInput.current) fileInput.current.value = '';
      void loadMine();
      requestAnimationFrame(() => resultRef.current?.focus());
      return;
    }
    if (result.stage === 'submit_failed') setReuseProofFileId(result.proofFileId);
    setFormError(result.message);
    requestAnimationFrame(() => resultRef.current?.focus());
  };

  const invoiceNumber = (id: string) => invoices.find((i) => i.id === id)?.number || 'Invoice';

  return (
    <section aria-labelledby={`pe-title-${uid}`} className="rounded-eds-lg border border-eds-border bg-eds-surface p-5 space-y-5">
      <div>
        <h3 id={`pe-title-${uid}`} className="font-eds-display text-lg font-semibold text-eds-fg">Submit payment evidence</h3>
        <p className="mt-1 text-sm text-eds-muted-fg max-w-2xl">
          Already paid a GIISDEC invoice? Submit your payment evidence here. GIISDEC Finance checks every
          submission before an invoice is marked paid — submitting evidence does not confirm your payment.
          If Finance cannot accept it, you can submit corrected evidence.
        </p>
      </div>

      {/* Payment instructions (authoritative, from the API) */}
      <div className="rounded-eds-md border border-eds-border bg-eds-surface-2 p-4 text-sm">
        <h4 className="font-semibold text-eds-fg">How to pay</h4>
        {instructionsError ? (
          <p className="mt-1 text-eds-muted-fg">The payment instructions could not be loaded right now. Please try again later, or contact GIISDEC Finance through Support.</p>
        ) : !instructions ? (
          <p className="mt-1 text-eds-muted-fg">Loading payment instructions…</p>
        ) : (
          <>
            <p className="mt-1 text-eds-fg">{instructions.instructions}</p>
            {instructions.configured ? (
              <dl className="mt-2 grid grid-cols-1 sm:grid-cols-3 gap-2">
                <div><dt className="text-xs text-eds-muted-fg">Account name</dt><dd className="font-medium text-eds-fg">{instructions.accountName}</dd></div>
                <div><dt className="text-xs text-eds-muted-fg">Mobile Money number</dt><dd className="font-medium text-eds-fg">{instructions.accountNumber}</dd></div>
                <div><dt className="text-xs text-eds-muted-fg">Network</dt><dd className="font-medium text-eds-fg">{instructions.network || '—'}</dd></div>
              </dl>
            ) : (
              <p className="mt-2 text-eds-muted-fg">GIISDEC Finance has not published the payment account details here yet. Contact Finance through Support before paying.</p>
            )}
          </>
        )}
      </div>

      {!invoicesLoaded ? (
        <p className="text-sm text-eds-muted-fg">Loading your invoices…</p>
      ) : payable.length === 0 ? (
        <p className="text-sm text-eds-muted-fg">You have no invoices awaiting payment, so there is nothing to submit evidence for.</p>
      ) : (
        <form onSubmit={onSubmit} noValidate className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Field label="Invoice (required)" htmlFor={ids.invoice}>
            <select
              id={ids.invoice}
              value={invoiceId}
              onChange={(e) => setInvoiceId(e.target.value)}
              required
              disabled={busy}
              className="h-11 w-full rounded-eds-md border border-eds-border bg-eds-surface px-3 text-sm text-eds-fg focus:outline-none focus-visible:ring-2 focus-visible:ring-eds-ring"
            >
              {payable.map((i) => (
                <option key={i.id} value={i.id}>
                  {i.number} — {money(i.balanceDue != null && i.balanceDue > 0 ? i.balanceDue : i.total, i.currency)} due
                </option>
              ))}
            </select>
          </Field>

          <Field label={`Amount paid${selected ? ` in ${selected.currency}` : ''} (required)`} htmlFor={ids.amount} hint="Enter exactly what you paid.">
            <Input id={ids.amount} type="number" inputMode="decimal" min="0.01" step="0.01" required value={amount}
              onChange={(e) => setAmount(e.target.value)} disabled={busy} aria-describedby={`${ids.amount}-hint`} />
          </Field>

          <Field label="Transaction reference" htmlFor={ids.ref} hint="The Mobile Money transaction ID from your payment confirmation. Finance uses it to find your payment.">
            <Input id={ids.ref} maxLength={160} value={providerTxnRef} onChange={(e) => setProviderTxnRef(e.target.value)}
              disabled={busy} autoComplete="off" aria-describedby={`${ids.ref}-hint`} />
          </Field>

          <Field label="Date and time of payment" htmlFor={ids.when}>
            <Input id={ids.when} type="datetime-local" className="focus-within:ring-2 focus-within:ring-eds-ring" value={paidAt} onChange={(e) => setPaidAt(e.target.value)} disabled={busy} />
          </Field>

          <Field label="Payer name" htmlFor={ids.payer}>
            <Input id={ids.payer} maxLength={200} value={payerName} onChange={(e) => setPayerName(e.target.value)} disabled={busy} autoComplete="name" />
          </Field>

          <Field label="Payer phone number" htmlFor={ids.phone}>
            <Input id={ids.phone} type="tel" maxLength={40} value={payerPhone} onChange={(e) => setPayerPhone(e.target.value)} disabled={busy} autoComplete="tel" />
          </Field>

          <div className="md:col-span-2">
            <Field label="Payment proof (required)" htmlFor={ids.file}
              hint={`A receipt, statement or screenshot of the payment. PDF, JPEG or PNG, up to ${formatBytes(PROOF_MAX_BYTES)}.`}
              error={fileError}>
              {file ? (
                <div className="flex flex-wrap items-center gap-3 rounded-eds-md border border-eds-border bg-eds-surface-2 px-3 py-2">
                  <FileText className="w-4 h-4 text-eds-muted-fg" aria-hidden="true" />
                  <span className="text-sm font-medium text-eds-fg break-all">{file.name}</span>
                  <span className="text-xs text-eds-muted-fg">{formatBytes(file.size)}</span>
                  {reuseProofFileId && <span className="text-xs text-eds-muted-fg">Uploaded — will be reused</span>}
                  <button type="button" onClick={removeFile} disabled={busy}
                    className="ml-auto inline-flex items-center gap-1 rounded-eds-sm px-2 py-1 text-xs font-semibold text-eds-fg hover:bg-eds-surface focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-eds-ring">
                    <X className="w-3.5 h-3.5" aria-hidden="true" /> Remove file
                  </button>
                </div>
              ) : null}
              <input
                ref={fileInput}
                id={ids.file}
                type="file"
                accept={PROOF_ACCEPT}
                required
                onChange={(e) => onPickFile(e.target.files && e.target.files[0] ? e.target.files[0] : null)}
                disabled={busy}
                aria-invalid={fileError ? true : undefined}
                aria-describedby={fileError ? `${ids.file}-error` : `${ids.file}-hint`}
                className="block w-full text-sm text-eds-fg file:mr-3 file:rounded-eds-md file:border file:border-eds-border file:bg-eds-surface-2 file:px-3 file:py-2 file:text-sm file:font-semibold file:text-eds-fg rounded-eds-md focus:outline-none focus-visible:ring-2 focus-visible:ring-eds-ring"
              />
            </Field>
          </div>

          <div className="md:col-span-2">
            <label htmlFor={ids.note} className="font-eds-body text-[13px] font-semibold text-eds-fg">Note for Finance (optional)</label>
            <textarea id={ids.note} maxLength={2000} rows={3} value={note} onChange={(e) => setNote(e.target.value)} disabled={busy}
              className="mt-1.5 w-full rounded-eds-md border border-eds-border bg-eds-surface px-3 py-2 text-sm text-eds-fg focus:outline-none focus-visible:ring-2 focus-visible:ring-eds-ring" />
          </div>

          <div className="md:col-span-2 flex flex-wrap items-center gap-3">
            <Button type="submit" disabled={busy || !!fileError}>
              {phase === 'uploading' ? 'Uploading proof…' : phase === 'submitting' ? 'Submitting…' : 'Submit for Finance verification'}
            </Button>
            <span className="text-xs text-eds-muted-fg">Your payment is confirmed only after GIISDEC Finance verifies this evidence.</span>
          </div>

          <div className="md:col-span-2" aria-live="polite" aria-atomic="true">
            {phase !== 'idle' && (
              <p className="sr-only">{phase === 'uploading' ? 'Uploading payment proof' : 'Submitting payment evidence'}</p>
            )}
          </div>
        </form>
      )}

      <div ref={resultRef} tabIndex={-1} className="focus:outline-none">
        {formError && (
          <div role="alert" className="flex items-start gap-2 rounded-eds-md border border-eds-danger/30 bg-eds-danger/5 p-3 text-sm text-eds-fg">
            <AlertTriangle className="mt-0.5 w-4 h-4 shrink-0 text-eds-danger" aria-hidden="true" />
            <span>
              {formError}
              {reuseProofFileId ? ' Your proof file is already uploaded — correct the details and submit again.' : ''}
            </span>
          </div>
        )}
        {submitted && (
          <div role="status" className="flex items-start gap-2 rounded-eds-md border border-eds-border bg-eds-surface-2 p-3 text-sm text-eds-fg">
            <Clock className="mt-0.5 w-4 h-4 shrink-0 text-eds-muted-fg" aria-hidden="true" />
            <span>
              <strong className="font-semibold">Submitted — {PENDING_LABEL}.</strong>{' '}
              Reference <span className="font-mono">{submitted.reference}</span>. GIISDEC Finance will review your evidence
              and you will be notified of the outcome.
            </span>
          </div>
        )}
      </div>

      {/* The investor's own submissions — authoritative status from the API */}
      <div>
        <h4 className="font-semibold text-eds-fg text-sm mb-2">Your payment evidence</h4>
        {mineError ? (
          <p className="text-sm text-eds-muted-fg">Your submitted evidence could not be loaded right now. This does not mean anything has changed.</p>
        ) : mine === null ? (
          <p className="text-sm text-eds-muted-fg">Loading…</p>
        ) : mine.length === 0 ? (
          <p className="text-sm text-eds-muted-fg">You have not submitted any payment evidence yet.</p>
        ) : (
          <ul className="divide-y divide-eds-border rounded-eds-md border border-eds-border">
            {mine.map((s) => (
              <li key={s.id} className="flex flex-wrap items-center gap-x-4 gap-y-1 px-3 py-2 text-sm">
                <span className="font-mono text-xs text-eds-fg">{s.payment_reference}</span>
                <span className="text-eds-fg">{invoiceNumber(s.invoice_id)}</span>
                <span className="text-eds-fg">{money(Number(s.amount_claimed), s.currency)}</span>
                <span className="text-xs text-eds-muted-fg">{new Date(s.created_at).toLocaleDateString()}</span>
                <span className="ml-auto inline-flex items-center gap-1 text-xs font-semibold text-eds-fg">
                  {s.status === 'confirmed' ? <CheckCircle2 className="w-3.5 h-3.5" aria-hidden="true" />
                    : s.status === 'rejected' ? <AlertTriangle className="w-3.5 h-3.5" aria-hidden="true" />
                    : <Clock className="w-3.5 h-3.5" aria-hidden="true" />}
                  {submissionStatusLabel(s.status)}
                </span>
                {s.status === 'rejected' && s.rejection_reason && (
                  <span className="basis-full text-xs text-eds-muted-fg">Finance note: {s.rejection_reason}</span>
                )}
              </li>
            ))}
          </ul>
        )}
      </div>
    </section>
  );
};

export default PaymentEvidenceForm;
